import React, { useRef, useMemo, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown, 
  PackageOpen, 
  Loader2 
} from 'lucide-react';
import { useStore, setState } from '../../hooks/useStore.js';
import { groupInventoryByProduct, sortProductGroups } from '../../utils/sorting.js';
import { parseSearchTokens, matchesSearchTokens, getItemSearchToken } from '../../utils/search.js';
import { getExpiryStatus } from '../../utils/date.js';
import { InventoryRow } from './InventoryRow.jsx';

export function InventoryVirtualTable({ 
  onEditItem, 
  onBorrowItem, 
  onDeleteProduct, 
  onViewDetails 
}) {
  const parentRef = useRef(null);

  const inventory = useStore(s => s.inventory) || [];
  const settings = useStore(s => s.settings) || {};
  const searchQuery = useStore(s => s.searchQuery) || '';
  const locationFilter = useStore(s => s.locationFilter) || 'all';
  const filter = useStore(s => s.filter) || 'all';
  const sortColumn = useStore(s => s.sortColumn) || 'name';
  const sortDirection = useStore(s => s.sortDirection) || 'asc';
  const selectedInventoryIds = useStore(s => s.selectedInventoryIds) || new Set();
  const isSyncing = useStore(s => s.cloudSyncInProgress);

  // Group, filter & sort items with useMemo for high-performance computation
  const filteredGroups = useMemo(() => {
    const activeItems = inventory.filter(item => !item.deleted);
    let groups = Array.from(groupInventoryByProduct(activeItems).values());

    // Search filter
    if (searchQuery.trim()) {
      const tokens = parseSearchTokens(searchQuery);
      groups = groups.filter(g => {
        const searchBlob = g.lots.map(l => getItemSearchToken(l)).join(' ');
        return matchesSearchTokens(searchBlob, tokens);
      });
    }

    // Location filter
    if (locationFilter && locationFilter !== 'all') {
      groups = groups.filter(g => g.lots.some(l => l.location === locationFilter));
    }

    // Status filter
    if (filter === 'expired') {
      groups = groups.filter(g =>
        g.lots.some(l => getExpiryStatus(l.expiryDate, settings.expiryWarningDate) === 'expired')
      );
    } else if (filter === 'near-expiry') {
      groups = groups.filter(g =>
        g.lots.some(l => {
          const s = getExpiryStatus(l.expiryDate, settings.expiryWarningDate);
          return s === 'expiring-soon' || s === 'expired';
        })
      );
    } else if (filter === 'low') {
      const threshold = Number(settings.lowStockThreshold) || 5;
      groups = groups.filter(g => g.totalQuantity <= threshold);
    }

    // Apply sorting
    return sortProductGroups(groups, sortColumn, sortDirection);
  }, [inventory, settings, searchQuery, locationFilter, filter, sortColumn, sortDirection]);

  // Virtualizer instance: seamlessly scales to 10,000+ rows
  const rowVirtualizer = useVirtualizer({
    count: filteredGroups.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 74,
    overscan: 8
  });

  const handleSort = (col) => {
    if (sortColumn === col) {
      setState({ sortDirection: sortDirection === 'asc' ? 'desc' : 'asc' });
    } else {
      setState({ sortColumn: col, sortDirection: 'asc' });
    }
  };

  const handleToggleSelect = useCallback((key) => {
    const current = new Set(selectedInventoryIds);
    if (current.has(key)) current.delete(key);
    else current.add(key);
    setState({ selectedInventoryIds: current });
  }, [selectedInventoryIds]);

  const allFilteredSelected = useMemo(() => {
    if (filteredGroups.length === 0) return false;
    return filteredGroups.every(g => selectedInventoryIds.has(g.key));
  }, [filteredGroups, selectedInventoryIds]);

  const handleToggleSelectAll = () => {
    const current = new Set(selectedInventoryIds);
    if (allFilteredSelected) {
      filteredGroups.forEach(g => current.delete(g.key));
    } else {
      filteredGroups.forEach(g => current.add(g.key));
    }
    setState({ selectedInventoryIds: current });
  };

  const renderSortIcon = (col) => {
    if (sortColumn !== col) {
      return <ArrowUpDown className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-600 ml-1" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 text-indigo-600 ml-1" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-indigo-600 ml-1" />
    );
  };

  if (filteredGroups.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200/80 bg-white/80 backdrop-blur-md p-12 text-center shadow-xs">
        {isSyncing && inventory.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 text-slate-500">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            <div className="text-sm font-semibold">กำลังดาวน์โหลดข้อมูลสต็อกจากคลาวด์...</div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 text-slate-500">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400">
              <PackageOpen className="w-7 h-7" />
            </div>
            <div className="text-base font-bold text-slate-800">ไม่พบรายการสินค้า</div>
            <div className="text-xs text-slate-500 max-w-sm">
              ไม่พบข้อมูลที่ตรงกับเงื่อนไขการค้นหาหรือตัวกรองที่เลือก ลองเปลี่ยนคำค้นหาหรือตัวกรอง
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/80 backdrop-blur-md shadow-xs overflow-hidden">
      <div className="table-touch-container">
        <div className="min-w-[700px] lg:min-w-0">
          {/* Sticky Table Header */}
          <div className="flex items-center bg-slate-100/90 border-b border-slate-300/80 px-2 sm:px-3 py-3 text-xs font-extrabold text-slate-800 uppercase tracking-wider select-none">
            <div className="w-9 sm:w-10 flex-shrink-0 flex items-center justify-center">
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={handleToggleSelectAll}
                className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                aria-label="เลือกทั้งหมด"
              />
            </div>

            <button
              onClick={() => handleSort('name')}
              className="flex-1 min-w-[140px] flex items-center text-left hover:text-slate-900 group cursor-pointer"
            >
              <span>ชื่อสินค้า / พัสดุ</span>
              {renderSortIcon('name')}
            </button>

            <button
              onClick={() => handleSort('brand')}
              className="hidden md:flex items-center w-20 lg:w-28 flex-shrink-0 text-left hover:text-slate-900 group cursor-pointer"
            >
              <span>ยี่ห้อ</span>
              {renderSortIcon('brand')}
            </button>

            <button
              onClick={() => handleSort('size')}
              className="hidden lg:flex items-center w-20 lg:w-24 flex-shrink-0 text-left hover:text-slate-900 group cursor-pointer"
            >
              <span>ขนาด/สเปค</span>
              {renderSortIcon('size')}
            </button>

            <button
              onClick={() => handleSort('quantity')}
              className="w-20 sm:w-24 lg:w-28 flex-shrink-0 flex items-center justify-end text-right hover:text-slate-900 group cursor-pointer pr-2 sm:pr-3"
            >
              <span>คงเหลือ</span>
              {renderSortIcon('quantity')}
            </button>

            <div className="hidden sm:block w-44 md:w-52 lg:w-64 flex-shrink-0 pr-2 sm:pr-3">
              <span>ข้อมูลล็อต & วันหมดอายุ</span>
            </div>

            <div className="w-28 sm:w-32 lg:w-36 flex-shrink-0 text-right pr-2">
              <span>จัดการ</span>
            </div>
          </div>

          {/* Virtual Scroll Container */}
          <div
            ref={parentRef}
            className="overflow-y-auto max-h-[calc(100vh-270px)] will-change-scroll touch-scroll"
          >
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative'
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const group = filteredGroups[virtualRow.index];
                const isSelected = selectedInventoryIds.has(group.key);

                return (
                  <InventoryRow
                    key={group.key}
                    group={group}
                    isSelected={isSelected}
                    onToggleSelect={handleToggleSelect}
                    onEditItem={onEditItem}
                    onBorrowItem={onBorrowItem}
                    onDeleteProduct={onDeleteProduct}
                    onViewDetails={onViewDetails}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
